import React from 'react';
import ScoringCategoryBadge from './ScoringCategoryBadge';
import { BreakdownEntry } from './useScoringEvents';

export type ScoreSummaryRowProps = Readonly<{
  score: number;
  overallCategory: string;
  breakdown: BreakdownEntry[];
}>;

type CodeRowProps = Readonly<{
  entry: BreakdownEntry;
  group: 'original' | 'added' | 'unknown';
}>;

type FlatDeductionRowProps = Readonly<{
  entry: BreakdownEntry;
}>;

type CodeTypeSectionProps = Readonly<{
  codeType: string;
  entries: BreakdownEntry[];
}>;

const CODE_TYPE_LABELS: Record<string, string> = {
  cpt: 'CPT',
  icd: 'ICD',
  modifier: 'Modifier'
};

const textStyles = {
  sectionLabel: {
    fontWeight: 600,
    fontSize: '11px',
    color: '#666',
    textTransform: 'uppercase' as const,
    display: 'block',
    marginBottom: '4px'
  },
  groupLabel: {
    fontWeight: 600,
    fontSize: '10px',
    color: '#888',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em'
  },
  codeChip: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: '12px',
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: '#fff',
    border: '1px solid #ddd'
  },
  bodyText: { fontSize: '12px', lineHeight: '16px' },
  mutedText: { fontSize: '12px', lineHeight: '16px', color: '#666' },
  scoreLabel: { fontWeight: 600, fontSize: '12px' },
  scoreHeader: {
    fontWeight: 600,
    fontSize: '11px',
    color: '#666',
    textTransform: 'uppercase' as const,
    textAlign: 'center' as const,
    paddingBottom: '4px'
  },
  scoreValue: { fontWeight: 700, fontSize: '20px', lineHeight: '1.6' },
  summaryTotal: { fontFamily: 'monospace', fontWeight: 700, fontSize: '16px' },
  summaryPenalty: {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '14px',
    textAlign: 'center' as const
  },
  emptyState: { fontSize: '12px', color: '#666', fontStyle: 'italic' as const }
};

const layoutStyles = {
  summary: {
    padding: '12px',
    marginBottom: '12px',
    backgroundColor: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '8px'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    alignItems: 'center'
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '4px',
    paddingBottom: '4px'
  },
  flatDeductionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '4px',
    paddingBottom: '4px'
  },
  codeTypeSection: { marginBottom: '12px' },
  codeGroup: { marginBottom: '4px' },
  panel: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '16px',
    backgroundColor: '#fafafa'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px'
  }
};

const SUMMARY_TYPES: { key: string; label: string }[] = [
  { key: 'cpt', label: 'CPT' },
  { key: 'icd', label: 'ICD' },
  { key: 'modifier', label: 'MOD' }
];

export const TOTAL_COLOR_BY_CATEGORY: Record<string, string> = {
  '0_IDEAL': '#26835B',
  '1_LOW': '#00838F',
  '2_MEDIUM': '#F9A825',
  '3_HIGH': '#F5A73F',
  '5_CRITICAL': '#E50000'
};

const getBreakdownEntryKey = (entry: BreakdownEntry) =>
  [
    entry.answerId,
    entry.codeType,
    entry.codeValue ?? '',
    entry.category,
    entry.displayText ?? '',
    entry.rubricPoints,
    entry.isOriginal ?? 'unknown'
  ].join('|');

const getAccentColor = (
  isOriginalRemoved: boolean,
  group: CodeRowProps['group']
) => {
  if (isOriginalRemoved) return '#E50000';
  if (group === 'added') return '#16a34a';
  return '#444';
};

const getSigil = (hasDeduction: boolean, group: CodeRowProps['group']) => {
  if (group === 'original' && hasDeduction) return '−';
  if (group === 'added') return '+';
  return '';
};

export function ScoreSummaryRow({
  score,
  overallCategory,
  breakdown
}: ScoreSummaryRowProps) {
  const penalty = (type: string) =>
    breakdown
      .filter((e) => e.codeType === type)
      .reduce((s, e) => s + e.rubricPoints, 0);

  const totalColor = TOTAL_COLOR_BY_CATEGORY[overallCategory] ?? '#212529';
  const fmt = (n: number) => (n > 0.001 ? `−${n.toFixed(2)}` : '0.00');

  return (
    <div style={layoutStyles.summary}>
      <div style={layoutStyles.summaryGrid}>
        <span style={textStyles.scoreHeader}>Total</span>
        <span style={textStyles.scoreHeader}>CPT</span>
        <span style={textStyles.scoreHeader}>ICD</span>
        <span style={textStyles.scoreHeader}>MOD</span>
        <span
          style={{
            ...textStyles.summaryTotal,
            color: totalColor,
            textAlign: 'center'
          }}
        >
          {Number(score).toFixed(2)}/10
        </span>
        {SUMMARY_TYPES.map((t) => {
          const p = penalty(t.key);
          return (
            <span
              key={t.key}
              style={{
                ...textStyles.summaryPenalty,
                color: p > 0.001 ? '#E50000' : '#adb5bd'
              }}
            >
              {fmt(p)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function CodeRow({ entry, group }: CodeRowProps) {
  const hasDeduction = entry.rubricPoints > 0.001;
  const isOriginalRemoved = group === 'original' && hasDeduction;
  const accentColor = getAccentColor(isOriginalRemoved, group);
  const sigil = getSigil(hasDeduction, group);

  return (
    <div style={layoutStyles.codeRow}>
      <span
        style={{
          width: 12,
          color: accentColor,
          fontWeight: 700,
          textAlign: 'center',
          fontSize: '12px'
        }}
      >
        {sigil}
      </span>
      <span
        style={{
          ...textStyles.codeChip,
          color: accentColor,
          textDecoration: isOriginalRemoved ? 'line-through' : 'none'
        }}
      >
        {entry.codeValue ?? '—'}
      </span>
      <span
        style={{
          ...textStyles.mutedText,
          flex: 1,
          fontStyle: entry.displayText ? 'normal' : 'italic'
        }}
      >
        {entry.displayText ?? '—'}
      </span>
      {hasDeduction && (
        <ScoringCategoryBadge value={entry.category} score={entry.rubricPoints} />
      )}
    </div>
  );
}

function FlatDeductionRow({ entry }: FlatDeductionRowProps) {
  return (
    <div style={layoutStyles.flatDeductionRow}>
      <span style={{ ...textStyles.bodyText, flex: 1 }}>
        {entry.displayText ?? '—'}
      </span>
      <span
        style={{ ...textStyles.mutedText, minWidth: 40, textAlign: 'right' }}
      >
        −{entry.rubricPoints}
      </span>
      <ScoringCategoryBadge value={entry.category} score={null} />
    </div>
  );
}

export function CodeTypeSection({ codeType, entries }: CodeTypeSectionProps) {
  const enriched = entries.some(
    (e) => e.codeValue != null || e.isOriginal != null
  );
  const original = entries.filter((e) => e.isOriginal === true);
  const added = entries.filter((e) => e.isOriginal === false);
  const unknown = entries.filter((e) => e.isOriginal == null);

  return (
    <div style={layoutStyles.codeTypeSection}>
      <span style={textStyles.sectionLabel}>
        {CODE_TYPE_LABELS[codeType] ?? codeType}
      </span>

      {!enriched &&
        entries.map((entry) => (
          <FlatDeductionRow key={getBreakdownEntryKey(entry)} entry={entry} />
        ))}

      {enriched && original.length > 0 && (
        <div style={layoutStyles.codeGroup}>
          <span style={textStyles.groupLabel}>Original</span>
          {original.map((e) => (
            <CodeRow key={getBreakdownEntryKey(e)} entry={e} group="original" />
          ))}
        </div>
      )}

      {enriched && added.length > 0 && (
        <div style={layoutStyles.codeGroup}>
          <span style={textStyles.groupLabel}>Added</span>
          {added.map((e) => (
            <CodeRow key={getBreakdownEntryKey(e)} entry={e} group="added" />
          ))}
        </div>
      )}

      {enriched &&
        unknown.length > 0 &&
        unknown.map((e) => (
          <CodeRow key={getBreakdownEntryKey(e)} entry={e} group="unknown" />
        ))}
    </div>
  );
}

